import { Context, Effect, Layer } from 'effect';
import { FetchHttpClient, HttpClient, type HttpClientError, HttpClientRequest } from 'effect/http';
import { EnvironmentService } from './environment';
import { type NetworkError, withTransportConventions } from './http-conventions';

/** Everything a request for a drawing can fail with. */
export type DrawingClientError = HttpClientError.HttpClientError | NetworkError;

/**
 * An `HttpClient` preconfigured for the public CDN the drawings are served from.
 *
 * A client of its own rather than `ApiClient` pointed elsewhere, and not because the two merely differ: everything that makes the API client an API client would break a request to this host. It is public, so a bearer token has nobody to prove anything to — and the CDN answers no `Access-Control-Allow-Headers`, so an
 * `Authorization` or `x-account-id` header would turn a simple GET into a preflighted request the browser then blocks outright. The API's 401 and 403 mapping would be wrong here too, since neither status means what it means there.
 *
 * What the two do share is how the exchange itself is conducted, and that is `withTransportConventions`: a non-2xx fails, a transient failure is retried, a dead transport becomes a `NetworkError`, and no trace headers go out — the last of which this host requires for the same CORS reason as above.
 *
 * Requests are made with paths relative to the environment's drawing origin, so callers spell out only the object — an account, an engine and a calculation, ending in .svg. What that path is made of belongs to whoever knows what a drawing is named after, not here.
 */
export class DrawingClient extends Context.Service<DrawingClient, HttpClient.HttpClient.With<DrawingClientError>>()('@acies/platform/DrawingClient', {
	/** Wraps whichever `HttpClient` is in context with the drawing origin and nothing else. */
	make: Effect.gen(function* () {
		const { drawingUrl } = yield* EnvironmentService;
		const httpClient = yield* HttpClient.HttpClient;
		return httpClient.pipe(
			HttpClient.mapRequest(HttpClientRequest.prependUrl(drawingUrl)),
			// No acceptJson and no headers of any kind: the CDN serves one content type, ignores
			// what is asked of it, and every header we could add is one CORS would have to allow.
			withTransportConventions,
		);
	}),
}) {
	/** The client itself, leaving its dependencies to the caller. */
	static readonly layerNoDeps: Layer.Layer<DrawingClient, never, EnvironmentService | HttpClient.HttpClient> = Layer.effect(this)(this.make);

	/** The client wired to the browser: fetch and hostname detection, and no account at all. */
	static readonly layer: Layer.Layer<DrawingClient> = DrawingClient.layerNoDeps.pipe(Layer.provide(FetchHttpClient.layer), Layer.provide(EnvironmentService.layer));
}
